/** Badge counters follow the TDG Account, not the machine.
 *
 *  `lifetime.ts` still owns the counting rule and still writes `bea.lifetime`
 *  on this device — nothing here changes what counts as a bookmark made, and a
 *  signed-out reader keeps counting exactly as before. What this module adds is
 *  ownership, which the counters needed the moment they started travelling:
 *  a badge is a claim about what a PERSON did, and `bea.lifetime` is one record
 *  on a machine two people can both sign in on. Without a stamp, the second one
 *  to sign in inherits the first one's levels — and then that borrowed record
 *  rides their account snapshot onto their own devices, where it looks exactly
 *  like something they earned.
 *
 *  Three cases, and the middle one is the whole reason this exists:
 *
 *  - **Already ours** — nothing to do. The double `currentProfile` emission of
 *    a single sign-in (cached identity, then the account copy) lands here.
 *  - **Unclaimed** — this device has counters but has never been stamped, so
 *    the account signing in ADOPTS them. That is the upgrade path, and it is
 *    what stops the first sync from resetting a reader who used the app for a
 *    year before ever making an account. Their counting really did happen, and
 *    nobody else has a claim on it.
 *  - **Someone else's** — park theirs under their id, restore ours from the
 *    vault, or start a blank record if this device has never held one for us.
 *
 *  A blank record opens at ZERO rather than at what the device is holding.
 *  `initLifetime` seeds from the bookmarks and highlights already here, on the
 *  grounds that holding one proves making one — but that is a claim about the
 *  DEVICE, and on somebody else's machine those are somebody else's bookmarks.
 *  Opening at zero costs a new reader nothing they earned (the counters have no
 *  history of them yet either way) and the Badge Center already says so in
 *  plain words: counted from today, arriving late rather than wrong.
 *
 *  Signing OUT changes nothing. The record keeps its owner and keeps counting,
 *  because the person still reading is the person who was signed in — and it is
 *  waiting for them, stamped, when they sign back in. */
import { persisted } from '../../core/state';
import { currentProfile } from '../account/accountService';
import { parkAndRestore } from '../accountVault';
import { BLANK_LIFETIME, lifetime, openLifetimeCounters, type LifetimeState } from './lifetime';

export interface LifetimeOwnerState {
  /** Account whose counters `bea.lifetime` currently holds ('' = unclaimed, so
   *  the next account to sign in inherits this device's existing record). */
  ownerId: string;
  /** Records parked for other accounts that have signed in on this device, so
   *  switching back restores a history instead of starting from nothing. */
  vault: Record<string, LifetimeState>;
}

/** Device bookkeeping, deliberately NOT part of an account snapshot — see the
 *  `LS_SKIP` list in `account/sync.ts` and the note in `services/accountVault.ts`. */
export const lifetimeOwner = persisted<LifetimeOwnerState>('lifetimeOwner', { ownerId: '', vault: {} });

/** Point `bea.lifetime` at the counters belonging to `internalId`.
 *  Returns true when the live record was swapped. */
export function claimLifetimeFor(internalId: string, now = new Date()): boolean {
  if (!internalId) return false;
  const st = lifetimeOwner.get();
  if (st.ownerId === internalId) return false;
  if (!st.ownerId) {
    lifetimeOwner.set({ ...st, ownerId: internalId });
    return false;
  }
  const { vault, restored } = parkAndRestore(st.vault, st.ownerId, lifetime.get(), internalId);
  lifetimeOwner.set({ ownerId: internalId, vault });
  // A restored record may predate a counter added since it was parked, and a
  // blank one has no counters open at all. Either way it is opened HERE, with
  // no openings: this reader has proven nothing on this machine.
  const mine: LifetimeState = restored ? { ...restored } : { ...BLANK_LIFETIME };
  lifetime.set(openLifetimeCounters(mine, {}, now) ?? mine);
  return true;
}

let started = false;

/** Wire badge ownership up.
 *
 *  **Call this BEFORE `initLifetime()`** (main.ts does). A claim that swaps the
 *  record leaves it fully opened, so the `initLifetime` that runs straight after
 *  finds nothing to open and cannot seed the incoming reader with the outgoing
 *  one's bookmark count. Idempotent; call once. */
export function initLifetimeOwner(): void {
  if (started) return;
  started = true;
  currentProfile.subscribe((p) => { if (p) claimLifetimeFor(p.internalId); }, true);
}
